import React, { useState, useEffect, useRef, useCallback } from 'react';
import HeaderLogged from './components/header_logged';
import Container from './components/container';
import Tabs from './components/Tabs';
import { FaThumbtack } from 'react-icons/fa';
import './SalesForecastPage.css';
import api from './api/client';

// FixedSeasonalityTable Component
const FixedSeasonalityTable = ({ nicheId, coefficients, updateCoefficient }) => {
    const [editingCell, setEditingCell] = useState(null);
    const [editValue, setEditValue] = useState('');

    console.log('FixedSeasonalityTable props:', { nicheId, coefficients });

    const handleDoubleClick = (month) => {
        setEditingCell(month);
        setEditValue(coefficients[month] || '0.00');
    };

    const handleKeyDown = (e, month) => {
        if (e.key === 'Enter') {
            const value = parseFloat(editValue) || 0;
            updateCoefficient(month, value);
            setEditingCell(null);
        }
    };

    const months = [
        'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];

    return (
        <div className="fixed-seasonality-table">
            <table className="w-full border-collapse">
                <thead>
                    <tr>
                        {months.map((month, index) => (
                            <th key={index} className="border p-2 bg-gray-100">
                                {month}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        {months.map((month, index) => {
                            const monthNum = index + 1;
                            const isEditing = editingCell === monthNum;
                            return (
                                <td
                                    key={monthNum}
                                    className="border p-2 text-center"
                                    onDoubleClick={() => handleDoubleClick(monthNum)}
                                >
                                    {isEditing ? (
                                        <input
                                            type="number"
                                            step="0.01"
                                            value={editValue}
                                            onChange={(e) => setEditValue(e.target.value)}
                                            onKeyDown={(e) => handleKeyDown(e, monthNum)}
                                            className="w-full p-1 border rounded"
                                            autoFocus
                                        />
                                    ) : (
                                        (coefficients[monthNum] || 0).toFixed(2)
                                    )}
                                </td>
                            );
                        })}
                    </tr>
                </tbody>
            </table>
        </div>
    );
};

// ForecastTable Component
const ForecastTable = ({ title, products, nicheId, seasonalityCoefficients, years, baseField, updateBaseValue, updateTrigger }) => {
    const [editingBase, setEditingBase] = useState(null);
    const [editingCell, setEditingCell] = useState(null);
    const [editValue, setEditValue] = useState('');
    const [pinnedColumns, setPinnedColumns] = useState({});
    const [pinnedData, setPinnedData] = useState({});

    useEffect(() => {
        api.get(`/sales_forecast_history?niche_category_id=${nicheId}&plan_or_fact=${baseField === 'base_value_plan'}`)
            .then(res => {
                console.log(`Sales forecast history fetch status for niche ${nicheId}: ${res.status}`);
                return res.data;
            })
            .then(data => {
                const pinned = {};
                const pinnedValues = {};
                data.forEach(item => {
                    const key = `${item.year}-${item.month_num}`;
                    if (item.isPinned) {
                        pinned[key] = true;
                    }
                    if (!pinnedValues[item.sku]) pinnedValues[item.sku] = {};
                    pinnedValues[item.sku][key] = item.sales;
                });
                setPinnedColumns(pinned);
                setPinnedData(pinnedValues);
            })
            .catch(err => console.error(`Error fetching pinned data for niche ${nicheId}:`, err));
    }, [nicheId, baseField]);

    const updateUnpinnedColumns = useCallback(() => {
        const updates = years
            .filter(year => year.visible)
            .map(year => {
                return Array.from({ length: 12 }, (_, index) => {
                    const monthNum = index + 1;
                    const columnKey = `${year.year}-${monthNum}`;
                    if (pinnedColumns[columnKey]) {
                        return null;
                    }
                    const data = products.map(product => {
                        const baseValue = Number(product[baseField]) || 0;
                        const coefficient = seasonalityCoefficients[monthNum] || 0;
                        const sales = Math.round(baseValue * coefficient);
                        return { sku: product.SKU, sales };
                    });
                    return { year: year.year, month_num: monthNum, isPinned: 0, data };
                });
            })
            .flat()
            .filter(update => update !== null);

        if (updates.length > 0) {
            api.post(`/batch_update_forecast`, {
                niche_category_id: nicheId,
                plan_or_fact: baseField === 'base_value_plan',
                updates
            })
                .then(res => {
                    console.log(`Batch update forecast fetch status for niche ${nicheId}: ${res.status}`);
                    return res.data;
                })
                .then(() => console.log('Unpinned columns updated successfully'))
                .catch(err => console.error(`Error updating unpinned columns for niche ${nicheId}:`, err));
        }
    }, [nicheId, baseField, products, seasonalityCoefficients, years, pinnedColumns]);

    useEffect(() => {
        if (products.length > 0 && pinnedColumns !== null && typeof pinnedColumns === 'object') {
            updateUnpinnedColumns();
        }
    }, [seasonalityCoefficients, products, pinnedColumns, updateTrigger, updateUnpinnedColumns]);

    const handlePinColumn = (year, monthNum) => {
        const planOrFact = baseField === 'base_value_plan';
        const data = products.map(product => {
            const baseValue = Number(product[baseField]) || 0;
            const coefficient = seasonalityCoefficients[monthNum] || 0;
            const sales = Math.round(baseValue * coefficient);
            return { sku: product.SKU, sales };
        });

        api.post(`/pin_forecast_column`, {
            niche_category_id: nicheId,
            year,
            month_num: monthNum,
            plan_or_fact: planOrFact,
            data,
            isPinned: 1
        })
            .then(res => {
                console.log(`Pin forecast column fetch status for niche ${nicheId}: ${res.status}`);
                return res.data;
            })
            .then(() => {
                setPinnedColumns(prev => ({
                    ...prev,
                    [`${year}-${monthNum}`]: true
                }));
                setPinnedData(prev => {
                    const newPinnedData = { ...prev };
                    data.forEach(item => {
                        if (!newPinnedData[item.sku]) newPinnedData[item.sku] = {};
                        newPinnedData[item.sku][`${year}-${monthNum}`] = item.sales;
                    });
                    return newPinnedData;
                });
            })
            .catch(err => console.error(`Error pinning column for niche ${nicheId}:`, err));
    };

    const handleDoubleClickCell = (sku, year, month) => {
        const columnKey = `${year}-${month}`;
        if (pinnedColumns[columnKey]) {
            setEditingCell({ sku, year, month });
            const currentValue = pinnedData[sku]?.[columnKey] || 0;
            setEditValue(currentValue.toString());
        }
    };

    const handleKeyDownEdit = (e, sku, year, month) => {
        if (e.key === 'Enter') {
            const value = parseFloat(editValue) || 0;
            updatePinnedValue(sku, year, month, value);
            setEditingCell(null);
        }
    };

    const updatePinnedValue = (sku, year, month, value) => {
        const planOrFact = baseField === 'base_value_plan';
        const data = [{ sku, sales: value }];

        api.post(`/pin_forecast_column`, {
            niche_category_id: nicheId,
            year,
            month_num: month,
            plan_or_fact: planOrFact,
            data,
            isPinned: 1
        })
            .then(res => {
                console.log(`Update pinned value fetch status for niche ${nicheId}: ${res.status}`);
                return res.data;
            })
            .then(() => {
                setPinnedData(prev => {
                    const newPinnedData = { ...prev };
                    if (!newPinnedData[sku]) newPinnedData[sku] = {};
                    newPinnedData[sku][`${year}-${month}`] = value;
                    return newPinnedData;
                });
            })
            .catch(err => console.error(`Error updating pinned value for niche ${nicheId}:`, err));
    };

    const handleDoubleClickBase = (sku) => {
        setEditingBase(sku);
        const product = products.find((p) => p.SKU === sku);
        const baseValue = product ? Number(product[baseField]) || 0 : 0;
        setEditValue(baseValue.toString());
    };

    const handleKeyDownBase = (e, sku) => {
        if (e.key === 'Enter') {
            const value = parseFloat(editValue) || 0;
            updateBaseValue(sku, value);
            setEditingBase(null);
        }
    };

    const months = [
        'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];

    return (
        <div className="forecast-table mb-8">
            <h3 className="text-lg font-semibold mb-2 text-center forecast-table-title">{title}</h3>
            <div className="sales-table-wrapper">
                <table className="w-full border-collapse">
                    <thead>
                        <tr>
                            <th className="border p-2 sticky left-0 bg-white z-10">Product</th>
                            {years.map((year) => (
                                <React.Fragment key={year.year}>
                                    {year.visible &&
                                        months.map((month, index) => {
                                            const monthNum = index + 1;
                                            const columnKey = `${year.year}-${monthNum}`;
                                            const isPinned = pinnedColumns[columnKey];
                                            return (
                                                <th
                                                    key={columnKey}
                                                    className={`border p-2 ${isPinned ? 'pinned-month' : ''}`}
                                                >
                                                    <div className="flex items-center justify-center gap-2">
                                                        {month} {year.year}
                                                        {!isPinned && (
                                                            <FaThumbtack
                                                                className="pin-icon cursor-pointer text-gray-600 hover:text-gray-800"
                                                                onClick={() => handlePinColumn(year.year, monthNum)}
                                                            />
                                                        )}
                                                    </div>
                                                </th>
                                            );
                                        })}
                                </React.Fragment>
                            ))}
                            <th className="border p-2 sticky right-0 base-calculation">Base Value</th>
                        </tr>
                    </thead>
                    <tbody>
                        {products.map((product) => (
                            <tr key={product.SKU}>
                                <td className="border p-2 sticky left-0 bg-white z-10">
                                    {product.brandID} - {product.name}
                                </td>
                                {years.map((year) => (
                                    <React.Fragment key={year.year}>
                                        {year.visible &&
                                            months.map((month, index) => {
                                                const monthNum = index + 1;
                                                const columnKey = `${year.year}-${monthNum}`;
                                                const isPinned = pinnedColumns[columnKey];
                                                let forecastValue;
                                                if (isPinned) {
                                                    forecastValue = pinnedData[product.SKU]?.[columnKey] || 0;
                                                } else {
                                                    const baseValue = Number(product[baseField]) || 0;
                                                    const coefficient = seasonalityCoefficients[monthNum] || 0;
                                                    forecastValue = baseValue * coefficient;
                                                }
                                                const isEditing = editingCell &&
                                                    editingCell.sku === product.SKU &&
                                                    editingCell.year === year.year &&
                                                    editingCell.month === monthNum;

                                                if (isPinned) {
                                                    return (
                                                        <td
                                                            key={columnKey}
                                                            className={`border p-2 pinned-month ${isEditing ? 'editing' : ''}`}
                                                            onDoubleClick={() => handleDoubleClickCell(product.SKU, year.year, monthNum)}
                                                        >
                                                            {isEditing ? (
                                                                <input
                                                                    type="number"
                                                                    step="1"
                                                                    value={editValue}
                                                                    onChange={(e) => setEditValue(e.target.value)}
                                                                    onKeyDown={(e) => handleKeyDownEdit(e, product.SKU, year.year, monthNum)}
                                                                    className="w-full p-1"
                                                                    autoFocus
                                                                />
                                                            ) : (
                                                                forecastValue.toFixed(0)
                                                            )}
                                                        </td>
                                                    );
                                                } else {
                                                    return (
                                                        <td key={columnKey} className="border p-2">
                                                            {forecastValue.toFixed(0)}
                                                        </td>
                                                    );
                                                }
                                            })}
                                    </React.Fragment>
                                ))}
                                <td
                                    className="border p-2 sticky right-0 base-calculation"
                                    onDoubleClick={() => handleDoubleClickBase(product.SKU)}
                                >
                                    {editingBase === product.SKU ? (
                                        <input
                                            type="number"
                                            step="1"
                                            value={editValue}
                                            onChange={(e) => setEditValue(e.target.value)}
                                            onKeyDown={(e) => handleKeyDownBase(e, product.SKU)}
                                            className="w-full p-1"
                                            autoFocus
                                        />
                                    ) : (
                                        (Number(product[baseField]) || 0).toFixed(0)
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

// SalesForecastPage Component
function SalesForecastPage() {
    const [niches, setNiches] = useState([]);
    const [activeTab, setActiveTab] = useState(null);
    const [seasonalityCoefficients, setSeasonalityCoefficients] = useState({});
    const [productsByNiche, setProductsByNiche] = useState({});
    const [forecastYears, setForecastYears] = useState([]);
    const [updateTrigger, setUpdateTrigger] = useState(0);
    const tableRefs = useRef({});

    const currentYear = new Date().getFullYear();

    useEffect(() => {
        // Fetch niche categories
        api.get(`/niche_categories`)
            .then(res => {
                console.log('Niche categories fetch status:', res.status);
                return res.data;
            })
            .then(data => {
                const nichesArray = Array.isArray(data) ? data : [];
                console.log('Fetched niches:', nichesArray);
                setNiches(nichesArray);
                setActiveTab(nichesArray[0]?.niche_name || null);

                // Fetch coefficients for each niche
                Promise.all(
                    nichesArray.map(niche => {
                        const primaryUrl = `/seasonality_fixed?niche_category_id=${niche.niche_category_id}`;
                        console.log(`Attempting to fetch coefficients from: ${primaryUrl}`);
                        return api.get(primaryUrl, { withCredentials: true })
                            .then(res => {
                                console.log(`Fetch status for niche ${niche.niche_category_id}: ${res.status}`);
                                return res.data;
                            })
                            .then(data => {
                                console.log(`Raw coefficients for niche ${niche.niche_category_id}:`, data);
                                return { nicheId: niche.niche_category_id, data };
                            })
                            .catch(err => {
                                console.error(`Error fetching coefficients for niche ${niche.niche_category_id} from ${primaryUrl}:`, err.message);
                                // Try without credentials
                                console.log(`Retrying fetch without credentials for niche ${niche.niche_category_id}`);
                                return api.get(primaryUrl)
                                    .then(res => {
                                        console.log(`No-credentials fetch status for niche ${niche.niche_category_id}: ${res.status}`);
                                        return res.data;
                                    })
                                    .then(data => {
                                        console.log(`No-credentials raw coefficients for niche ${niche.niche_category_id}:`, data);
                                        return { nicheId: niche.niche_category_id, data };
                                    })
                                    .catch(noCredErr => {
                                        console.error(`Error fetching coefficients without credentials for niche ${niche.niche_category_id}:`, noCredErr.message);
                                        // Fallback to alternative endpoint
                                        const fallbackUrl = `/api/seasonality_fixed?niche_category_id=${niche.niche_category_id}`;
                                        console.log(`Attempting fallback fetch from: ${fallbackUrl}`);
                                        return api.get(fallbackUrl)
                                            .then(res => {
                                                console.log(`Fallback fetch status for niche ${niche.niche_category_id}: ${res.status}`);
                                                return res.data;
                                            })
                                            .then(data => {
                                                console.log(`Fallback raw coefficients for niche ${niche.niche_category_id}:`, data);
                                                return { nicheId: niche.niche_category_id, data };
                                            })
                                            .catch(fallbackErr => {
                                                console.error(`Error fetching coefficients for niche ${niche.niche_category_id} from fallback ${fallbackUrl}:`, fallbackErr.message);
                                                return { nicheId: niche.niche_category_id, data: [] };
                                            });
                                    });
                            });
                    })
                )
                    .then(results => {
                        const grouped = results.reduce((acc, { nicheId, data }) => {
                            console.log(`Processing data for niche ${nicheId}:`, data);
                            acc[nicheId] = Array.isArray(data)
                                ? data.reduce((monthAcc, item) => {
                                      console.log(`Processing item for niche ${nicheId}:`, item);
                                      const month = parseInt(item.month_num, 10);
                                      const coefficient = parseFloat(item.seasonality_coefficient);
                                      console.log(`Parsed month: ${month}, coefficient: ${coefficient}`);
                                      if (month >= 1 && month <= 12 && !isNaN(coefficient)) {
                                          monthAcc[month] = coefficient;
                                          console.log(`Assigned monthAcc[${month}] = ${coefficient}`);
                                      } else {
                                          console.log(`Skipped item for niche ${nicheId}, month: ${month}, coefficient: ${coefficient}`);
                                      }
                                      return monthAcc;
                                  }, {})
                                : {};
                            return acc;
                        }, {});
                        console.log('Transformed seasonality coefficients:', grouped);
                        setSeasonalityCoefficients(grouped);
                    })
                    .catch(err => {
                        console.error('Error processing coefficients:', err);
                        setSeasonalityCoefficients({});
                    });
            })
            .catch(err => {
                console.error('Error fetching niches:', err);
                setNiches([]);
            });

        // Fetch products with base values
        api.get(`/products_with_base`)
            .then(res => {
                console.log('Products fetch status:', res.status);
                return res.data;
            })
            .then(data => {
                const grouped = data.reduce((acc, item) => {
                    const nicheId = item.niche_category_id;
                    if (!acc[nicheId]) acc[nicheId] = [];
                    acc[nicheId].push(item);
                    return acc;
                }, {});
                setProductsByNiche(grouped);
            })
            .catch(err => {
                console.error('Error fetching products:', err);
                setProductsByNiche({});
            });

        // Set years for ForecastTable (current and next year)
        setForecastYears([
            { year: currentYear - 1, visible: true },
            { year: currentYear, visible: true },
            { year: currentYear + 1, visible: true }
        ]);
    }, [currentYear]);

    const updateCoefficient = (nicheId, month, value) => {
        api.post(`/seasonality_fixed`, {
            niche_category_id: nicheId,
            month_num: month,
            seasonality_coefficient: value
        }, { withCredentials: true })
            .then(res => {
                console.log('Update coefficient fetch status:', res.status);
                return res.data;
            })
            .then(() => {
                setSeasonalityCoefficients(prev => ({
                    ...prev,
                    [nicheId]: {
                        ...prev[nicheId],
                        [month]: value
                    }
                }));
            })
            .catch(err => console.error('Error updating coefficient:', err));
    };

    const updateBaseValue = (sku, value, type, nicheId) => {
        const field = type === 'fact' ? 'base_value_fact' : 'base_value_plan';
        api.post(`/update_base_value`, { sku, field, value })
            .then(res => {
                console.log('Update base value fetch status:', res.status);
                return res.data;
            })
            .then(() => {
                setProductsByNiche(prev => {
                    const updatedProducts = prev[nicheId].map(product =>
                        product.SKU === sku ? { ...product, [field]: value } : product
                    );
                    return { ...prev, [nicheId]: updatedProducts };
                });
            })
            .catch(err => console.error('Error updating base value:', err));
    };

    const toggleYear = (year) => {
        setForecastYears(prev => prev.map(y => y.year === year ? { ...y, visible: !y.visible } : y));
    };

    const tabs = niches.map(niche => ({
        name: niche.niche_name,
        label: niche.niche_name,
        content: (
            <div className="p-4">
                <button
                    onClick={() => setUpdateTrigger(prev => prev + 1)}
                    className="mb-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                >
                    Update Forecast Data
                </button>
                <FixedSeasonalityTable
                    nicheId={niche.niche_category_id}
                    coefficients={seasonalityCoefficients[niche.niche_category_id] || {}}
                    updateCoefficient={(month, value) => updateCoefficient(niche.niche_category_id, month, value)}
                />
                <ForecastTable
                    title="Forecast Table (Plan)"
                    products={productsByNiche[niche.niche_category_id] || []}
                    nicheId={niche.niche_category_id}
                    seasonalityCoefficients={seasonalityCoefficients[niche.niche_category_id] || {}}
                    years={forecastYears}
                    baseField="base_value_plan"
                    updateBaseValue={(sku, value) => updateBaseValue(sku, value, 'plan', niche.niche_category_id)}
                    updateTrigger={updateTrigger}
                />
                <ForecastTable
                    title="Forecast Table (Fact)"
                    products={productsByNiche[niche.niche_category_id] || []}
                    nicheId={niche.niche_category_id}
                    seasonalityCoefficients={seasonalityCoefficients[niche.niche_category_id] || {}}
                    years={forecastYears}
                    baseField="base_value_fact"
                    updateBaseValue={(sku, value) => updateBaseValue(sku, value, 'fact', niche.niche_category_id)}
                    updateTrigger={updateTrigger}
                />
            </div>
        )
    }));

    return (
        <>
            <HeaderLogged />
            <Container>
                <h2 className="section__title" style={{ marginTop: 150 }}>
                    Sales Forecast
                </h2>
                <p className="section__subtitle" style={{ marginBottom: 40 }}>Manage Sales Forecast Details</p>
                {niches.length > 0 ? (
                    <Tabs
                        tabs={tabs}
                        activeTab={activeTab}
                        setActiveTab={setActiveTab}
                        tableRefs={tableRefs}
                        className="sales-forecast-tabs"
                    />
                ) : (
                    <p>{niches === null ? 'Error loading niches' : 'No niches available'}</p>
                )}
            </Container>
        </>
    );
}

export default SalesForecastPage;